<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Customer Price List" :avatarImage="avatarImage" :avatarCircleMode="false" :loading="loading">
          <h4>{{customerData.name}}</h4>
          
          <OverviewBoxList v-show="!isNewPricelist">
            <OverviewBoxListItem title="Status">
              <span :class="statusInfo.class">{{statusInfo.label}}</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem v-if="isActive" title="Active Since">{{customerPriceList.priceList.activeStartedDate | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem v-if="isScheduled" title="Scheduled">{{customerPriceList.priceList.scheduleStartDate | formatDate}}</OverviewBoxListItem>
            <template v-if="(!isApproved || isScheduled) && !isArchived && customerPriceList.priceList">
              <OverviewBoxListItem v-if="customerPriceList.priceList && customerPriceList.priceList.stockPriceRequestGroup" title="Price Update Request">
                <LinkPriceRequest :name="`#${customerPriceList.priceList.stockPriceRequestGroup.sequenceNumber || 'Unknown'}`" :id="customerPriceList.priceList.stockPriceRequestGroupId" />
              </OverviewBoxListItem>
              <OverviewBoxListItem title="Pricelist Verified">
                {{customerPriceList.priceList.requestStatusVerified ? 'Yes' : 'No'}}
              </OverviewBoxListItem>
            </template>
            <OverviewBoxListItem title="Linked Price Source" v-if="linkedDataSourceRequestGroup">
              <LinkPriceRequest :name="`#${linkedDataSourceRequestGroup.sequenceNumber || 'Unknown'}`" :id="linkedDataSourceRequestGroup.id" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created By" v-if="customerPriceList.priceList && customerPriceList.priceList.user">
              <LinkUser :id="customerPriceList.priceList.user.id" :name="customerPriceList.priceList.user.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On" v-if="customerPriceList.priceList">{{customerPriceList.priceList.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated" v-if="customerPriceList.priceList">{{customerPriceList.priceList.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

        <OverviewActionBox v-if="!isNewPricelist && !editing && !loading">
          <OverviewActionButton class="btn-default" v-if="isApproved && !isArchived" @click="beginEditing()" icon="fa-pencil">Edit Price List</OverviewActionButton>
          <OverviewActionButton class="btn-default" v-if="!isApproved && !isArchived" @click="beginEditing()" icon="fa-pencil">Amend Pending Price List</OverviewActionButton>
          <OverviewActionButton class="btn-success" v-if="isApproved" @click="displayCustomerPricingReport()" icon="fa-clipboard-list">Display Price List Report</OverviewActionButton>
          <OverviewActionButton class="btn-success" v-if="isApproved" @click="emailCustomerPricingReport()" icon="fa-envelope">Email Price List</OverviewActionButton>
          <OverviewActionButton class="btn-warning" v-if="isApproved && !isArchived && !isScheduled" @click="archiveCustomerPriceList()" icon="fa-times">Cancel Price List</OverviewActionButton>
          <OverviewActionButton class="btn-success" 
            v-if="userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval) && (!isApproved || isScheduled) && !isArchived && customerPriceList.priceList"
            icon="fa-money-check-edit-alt"
            :link="'/pricing/requests/' + customerPriceList.priceList.stockPriceRequestGroupId">
            View Price Request
          </OverviewActionButton>
        </OverviewActionBox>

        <ContentBox title="Editing" v-if="editing">
          <template v-slot:content>
            <div class="row">
              <div class="col-md-12">
                <button type="button" class="btn btn-primary btn-sm btn-block" @click.prevent="submitChanges()">Submit Changes</button>
                <button type="button" class="btn btn-default btn-sm btn-block" @click.prevent="cancelEditing()">Cancel Editing</button>
              </div>
            </div>
          </template>
        </ContentBox>
      </div>

      <div class="col-sm-10">
        <CustomerPriceListEditor
          ref="customerPriceListEditor"
          :editing="editing"
          :priceListId="priceListId"
          :contactId="contactId"
          :priceListIntegrationType="priceListIntegrationType"
          @customerLoaded="customerData = $event"
          @priceListLoaded="customerPriceList = $event; loading=false;"
          @priceGroupOverrideSettingsUpdated="priceListPriceGroupOverrideSettings = $event" />
      </div>
    </div>
  </div>
</template>


<script>
import _ from 'lodash'
import { round } from 'mathjs'
import * as Types from '@/store/types'
import pdfMixin from '@/mixins/pdf'

import Utils from '@/utils/utils'

import DataDisplayMixin from '@/mixins/dataDisplay'
import CustomerPriceListEditor from '@/modules/pricing/components/customerPriceListEditor'

import PriceListDataCell from '@/modules/pricing/components/priceListDataCell'
import StockPriceListRatePopover from '@/modules/pricing/components/stockPriceListRatePopover'
import StockPriceListPricePopover from '@/modules/pricing/components/stockPriceListPricePopover'

import CustomerStockPriceGroupList from '@/modules/pricing/components/customerStockPriceGroupList'

import CustomerPriceListUpdateDialog from '@/modules/pricing/views/customerPriceListUpdateDialog.vue'
import CustomerPriceListEmailDialog from '@/modules/pricing/views/customerPriceListEmailDialog.vue'
import CustomerPriceListSetupDialog from '@/modules/pricing/views/customerPriceListSetupDialog'

import { LinkTypes, InventoryStockTypes, StockPriceTypes, CustomerOverrideTypes, CustomerOverrideLevelTypes, ApprovalTypes, PriceListIntegrationTypes } from '@/store/types'

export default {
  name: "CustomerPriceList",
  mixins: [pdfMixin, DataDisplayMixin],
  components: {
    PriceListDataCell,
    StockPriceListRatePopover,
    StockPriceListPricePopover,
    CustomerStockPriceGroupList,
    CustomerPriceListEditor
  },
  props: {
  },
  data() {
    return {
      contactId: parseInt(this.$route.params.customerId || 0),
      priceListId: parseInt(this.$route.params.id || 0),
      customerData: {},
      customerPriceList: {},
      priceListPriceGroupOverrideSettings: [],
      priceListIntegrationType: PriceListIntegrationTypes.seperatePriceRequest,

      loading: false,
      editing: false,
    }
  },  
  computed: {
    avatarImage() {
      return '/glass/img/customer-pricing-pricelist.png'
    },
    activityStreamURL() {
      if(!this.customerData.id || !this.$route.params.id)
        return ''

      return `/pricing/customer/${this.customerData.id}/pricelists/${this.$route.params.id}/activitystream`
    },
    isNewPricelist() {
      return !this.$route.params.id
    },
    isArchived() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isArchived
    },
    isActive() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isActive
    },
    isScheduled() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isScheduled
    },
    isApproved() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isApproved
    },
    linkedDataSourceRequestGroup() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.sourceLinkRequestGroup
    },
    statusInfo() {
      if(!this.$data.customerPriceList.priceList)
        return {}
      if(this.$data.customerPriceList.priceList.isArchived)
        return { class: 'status-archive', label: 'Archived' }
      if(!this.$data.customerPriceList.priceList.isApproved)
        return { class: 'status-pending', label: 'Pending Approval' }
      if(this.$data.customerPriceList.priceList.isScheduled)
        return { class: 'status-pending', label: 'Scheduled' }
      if(this.$data.customerPriceList.priceList.isActive)
        return { class: 'status-active', label: 'Active' }
      

      return {}
    }
  },
  created() {
    this.LinkTypes = LinkTypes

    this.priceListId = parseInt(this.$route.params.id || 0)
    this.contactId = parseInt(this.$route.params.customerId || 0)
    this.priceListIntegrationType = this.$route.query.priceListIntegrationType || PriceListIntegrationTypes.seperatePriceRequest
  },
  mounted() {
    this.loading = true
    
    // If a new price list, go straight into editing it
    if(this.$route.params.id == null)
      this.beginEditing()
  },
  beforeRouteUpdate (to, from, next) {
    this.priceListId = parseInt(this.$route.params.id || 0)
    this.contactId = parseInt(this.$route.params.customerId || 0)
    next()
    this.reloadData()
  },
  methods: {
    reloadData() {
      this.loading = true
      this.priceListIntegrationType = this.$route.query.priceListIntegrationType || PriceListIntegrationTypes.seperatePriceRequest
      this.$refs.customerPriceListEditor.reloadData()
    },

    beginEditing() {
      // Run check for pending price requests & how this relates to that
      // Ignore if amending a pending price list
      if(this.isApproved && !this.isArchived && this.$route.params.id != null) {
        this.displayModal(CustomerPriceListSetupDialog)
        .then((result) => {
          if(result) {

            this.priceListIntegrationType = result
            this.$data.editing = true
          }
        })
        return
      }

      this.$data.editing = true
    },
    cancelEditing() {
      this.$data.editing = false

      // If a new price list, just go back to the contact page
      if(this.$route.params.id == null) {
        this.$router.push(`/contacts/${this.$route.params.customerId}`)
        return
      }

      // Reset data back to actual
      this.reloadData()
    },
    submitChanges() {

      // If editing a pending price list, just update/amend the current price list directly
      var amendPriceList = false
      if(!this.isNewPricelist && !this.isApproved && !this.isActive) {
        amendPriceList = true
      }

      var updateListData = this.$refs.customerPriceListEditor.buildServerPriceListUpdateListData()

      this.displayModal(CustomerPriceListUpdateDialog, { 
        customerName: this.$data.customerData.name,
        contactId: parseInt(this.$route.params.customerId),
        customerPriceListId: parseInt(this.$route.params.id),
        updateListData: updateListData,
        priceGroupOverrideSettings: this.priceListPriceGroupOverrideSettings || [],
        priceListIntegrationType: this.priceListIntegrationType,
        amendPriceList: amendPriceList
      })
      .then((updatedPriceListId) => {
        // Redirect to the new/current pricelist
        if(updatedPriceListId != null) {
          if(updatedPriceListId != this.$route.params.id) {
            // Force refresh
            this.openLinkDirectly(`/pricing/customer/${this.$route.params.customerId}/pricelist/${updatedPriceListId}`)
          } else {
            this.cancelEditing()
          }
          this.successToast('Price List updated')
        }
      })
    },
    displayCustomerPricingReport() {
      this.displayReportPDF(`/contacts/${this.$data.customerData.id}/customer/pricelist/${this.$route.params.id ? this.$route.params.id : 'default'}/report`)
    },
    emailCustomerPricingReport() {
      this.displayModal(CustomerPriceListEmailDialog, { 
        contactData: this.$data.customerData,
        contactId: parseInt(this.$route.params.customerId),
        customerPriceListId: parseInt(this.$route.params.id),
      })
      .then((success) => {
        if(success)
          this.successToast('Customer Emailed')
      })      
    },
    archiveCustomerPriceList() {
      this.confirmDeleteMessage('Cancel Price List?', 'Are you sure you want to cancel this price list? The price list will be archived and no longer active')
      .then(success => {
        if(success) {
          this.displayProcessingDialog({
            title: 'Archiving Price List',
            message: 'Processing',
            icon: 'fa-tag',
            onBeforeOpen: (instance) => {
              this.$apiService.pricingService.archiveCustomerPriceList(this.$route.params.customerId, this.$route.params.id)
              .then(response => {
                instance.hide()

                this.successToast('Price List Archived')
                this.$router.push(`/contacts/${this.$route.params.customerId}`)
              })
              .catch(err => {
                instance.hide()
                this.displayNetworkError(err)
              })
            }
          })
        }
      })
    },
  }
}
</script>